import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { Card, Chip, Grid, Pagination, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { getNewsList } from '../../api/microcms/news';
import { screenType } from '../../constants/enum';

import CustomLoading from './CustomLoading';

import type { NewsType } from '../../api/microcms/news';

const styles = {
  card: {
    '@media screen and (max-width:480px)': {
      margin: '16px',
      padding: '0 0 32px 0'
    },
    border: '1px solid #DBDBE0',
    borderRadius: '4px',
    boxShadow: 0,
    margin: '24px',
    padding: '16px 40px 32px'
  },
  pagination: {
    '.MuiPagination-ul': {
      justifyContent: 'center'
    },
    mt: '32px'
  },
  row: {
    alignItems: 'center',
    borderBottom: '1px solid #DBDBE0',
    columnGap: '38px',
    cursor: 'pointer',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    padding: '16px 12px'
  },
  tag: {
    borderRadius: '4px',
    fontSize: '12px',
    height: '24px',
    ml: '12px'
  }
};

interface Props {
  screen: number;
}

const MypageNewsList = (props: Props): React.ReactElement => {
  const { screen } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [page, setPage] = useState(1);
  const perPage = 7;

  const [newsList, setNewsList] = useState<NewsType[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    void (async () => {
      setLoading(true);
      const data = await getNewsList();
      setNewsList(data);
      setLoading(false);
    })();
  }, []);

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    value: number
  ): void => {
    setPage(value);
  };

  const startItemIndex = (page - 1) * perPage;
  const endItemIndex = startItemIndex + perPage;
  const currentNews = newsList.slice(startItemIndex, endItemIndex);

  const chipColor = (category: string): object => {
    switch (category) {
      case t('mypage.news.category.1'):
        return {
          backgroundColor: '#EBF5FA',
          border: '1px solid #0A7EBF',
          color: '#0A7EBF'
        };
      case t('mypage.news.category.2'):
        return {
          backgroundColor: '#FBF3EB',
          border: '1px solid #D06604',
          color: '#D06604'
        };
      case t('mypage.news.category.3'):
        return {
          backgroundColor: '#ECF5F0',
          border: '1px solid #177D46',
          color: '#177D46'
        };
      case t('mypage.news.category.4'):
        return {
          backgroundColor: '#F3EBFA',
          border: '1px solid #6A00BD',
          color: '#6A00BD'
        };
      case t('mypage.news.category.5'):
        return {
          backgroundColor: '#FAECEC',
          border: '1px solid #BB1715',
          color: '#BB1715'
        };
      default:
        return {
          backgroundColor: '#F3F3F3',
          border: '1px solid #757575',
          color: '#757575'
        };
    }
  };

  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    const year = date.getFullYear();
    const month = date.getMonth() + 1;
    const day = date.getDate();

    return `${year}/${month}/${day}`;
  };

  return (
    <>
      <CustomLoading loading={loading} />
      <Grid>
        <Card sx={styles.card}>
          {currentNews.map((item, index) => (
            <Grid
              key={index}
              sx={styles.row}
              onClick={() => {
                if (screen === screenType.client) {
                  navigate(`/client/mypage/news/${item.id}`);
                } else if (screen === screenType.publisher) {
                  navigate(`/publisher/mypage/news/${item.id}`);
                } else if (screen === screenType.agent) {
                  navigate(`/agent/mypage/news/${item.id}`);
                }
              }}
            >
              <Grid container flexDirection={'column'} rowGap={'8px'}>
                <Grid container flexDirection={'row'}>
                  <Typography>{formatDate(item.publishedAt)}</Typography>
                  <Chip
                    label={item.category}
                    sx={[styles.tag, chipColor(item.category[0])]}
                  />
                </Grid>
                <Typography>{item.title}</Typography>
              </Grid>
              <ChevronRightIcon />
            </Grid>
          ))}
          <Pagination
            count={Math.ceil(newsList.length / perPage)}
            page={page}
            onChange={handlePageChange}
            sx={styles.pagination}
          />
        </Card>
      </Grid>
    </>
  );
};

export default MypageNewsList;
